import { 
    Box, 
    Grid, 
    Text, 
    useColorModeValue,
    keyframes,
    Flex,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    HStack,
    Icon
} from "@chakra-ui/react";
import React from "react";
import { 
    SiChakraui, SiCss3, SiExpress, SiGithub, SiHtml5, SiJavascript, 
    SiMongodb, SiNodedotjs, SiReact, SiRedux, SiGit, 
    SiPostman, SiTailwindcss, SiVercel 
} from "react-icons/si";
import { 
    FaTools, 
    FaBrain, 
    FaUsers, 
    FaComments, 
    FaClock, 
    FaLightbulb,
    FaHandshake,
    FaChartLine
} from "react-icons/fa";

const fadeInUp = keyframes`
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
    from { opacity: 0; transform: translateX(-30px); }
    to { opacity: 1; transform: translateX(0); }
`;

const float = keyframes`
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
`;

const Techstack = () => {
    const sectionBg = useColorModeValue("gray.50", "gray.900");
    const cardBg = useColorModeValue("white", "gray.800");
    const cardBorder = useColorModeValue("gray.200", "gray.700");
    const textColor = useColorModeValue("gray.600", "gray.300");
    const titleColor = useColorModeValue("gray.800", "white");
    const labelBg = useColorModeValue("blue.50", "blue.900");
    const labelBorder = useColorModeValue("gray.300", "gray.600");
    const tabBg = useColorModeValue("white", "gray.800");
    const tabSelectedBg = useColorModeValue("blue.500", "blue.500");
    const tabColor = useColorModeValue("gray.500", "gray.400");
    const tabSelectedColor = "white";
    const tabHoverColor = useColorModeValue("blue.600", "blue.300");

    const techSkills = [
        { name: "HTML5", icon: SiHtml5, color: "#E34F26", level: 95 },
        { name: "CSS3", icon: SiCss3, color: "#1572B6", level: 90 },
        { name: "JavaScript", icon: SiJavascript, color: "#F7DF1E", level: 90 },
        { name: "React", icon: SiReact, color: "#61DAFB", level: 88 },
        { name: "Redux", icon: SiRedux, color: "#764ABC", level: 85 },
        { name: "Node.js", icon: SiNodedotjs, color: "#339933", level: 82 },
        { name: "Express", icon: SiExpress, color: "#000000", level: 80 },
        { name: "MongoDB", icon: SiMongodb, color: "#47A248", level: 78 },
        { name: "Chakra UI", icon: SiChakraui, color: "#319795", level: 90 },
        { name: "Tailwind", icon: SiTailwindcss, color: "#06B6D4", level: 85 },
        { name: "Git", icon: SiGit, color: "#F05032", level: 88 },
        { name: "GitHub", icon: SiGithub, color: "#181717", level: 90 },
        { name: "Postman", icon: SiPostman, color: "#FF6C37", level: 85 },
        { name: "Vercel", icon: SiVercel, color: "#000000", level: 80 },
    ];

    const softSkills = [
        { name: "Problem Solving", icon: FaBrain, color: "purple.500", level: 95, desc: "Analytical thinking & creative solutions" },
        { name: "Communication", icon: FaComments, color: "blue.500", level: 90, desc: "Clear verbal & written communication" },
        { name: "Teamwork", icon: FaUsers, color: "green.500", level: 92, desc: "Collaboration & team coordination" },
        { name: "Time Management", icon: FaClock, color: "orange.500", level: 88, desc: "Prioritization & deadline management" },
        { name: "Adaptability", icon: FaLightbulb, color: "yellow.500", level: 90, desc: "Quick learning & flexibility" },
        { name: "Leadership", icon: FaHandshake, color: "red.500", level: 85, desc: "Mentoring & decision making" },
        { name: "Critical Thinking", icon: FaChartLine, color: "teal.500", level: 88, desc: "Logical analysis & evaluation" },
    ];

    return (
        <Box
            id="skills"
            width="100%"
            py={16}
            bg={sectionBg}
            position="relative"
            overflow="hidden"
        >
            {/* Background Decorative Elements */}
            <Box
                position="absolute"
                top="10%"
                left="-10%"
                width="300px"
                height="300px"
                bg="blue.100"
                borderRadius="full"
                opacity={0.2}
                filter="blur(80px)"
                pointerEvents="none"
            />
            <Box
                position="absolute"
                bottom="10%"
                right="-10%"
                width="400px"
                height="400px"
                bg="purple.100"
                borderRadius="full"
                opacity={0.15}
                filter="blur(100px)"
                pointerEvents="none"
            />

            <Box maxWidth="1200px" mx="auto" px={{ base: 4, md: 8 }} position="relative" zIndex={1}>
                {/* Section Header */}
                <Flex
                    direction="column"
                    align="center"
                    mb={12}
                    animation={`${slideIn} 0.6s ease-out`}
                >
                    <Flex
                        align="center"
                        gap={3}
                        textTransform="uppercase"
                        border="1px solid"
                        borderColor={labelBorder}
                        fontWeight={600}
                        fontSize="sm"
                        bg={labelBg}
                        p={2}
                        px={4}
                        rounded="md"
                        mb={4}
                        position="relative"
                        overflow="hidden"
                    >
                        <Icon as={FaTools} />
                        My Skills
                    </Flex>
                    
                    <Text
                        fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                        fontWeight={800}
                        textTransform="uppercase"
                        letterSpacing="wider"
                        color={titleColor}
                        mb={2}
                        textAlign="center"
                    >
                        Technical & Soft Skills
                    </Text>
                    <Box
                        width="60px"
                        height="4px"
                        bgGradient="linear(to-r, blue.400, purple.500)"
                        borderRadius="full"
                        mb={4}
                    />
                    <Text
                        fontSize={{ base: "md", md: "lg" }}
                        color={textColor}
                        textAlign="center"
                        maxW="600px"
                    >
                        A combination of technical expertise and interpersonal abilities
                    </Text>
                </Flex>

                {/* Tabs Navigation */}
                <Tabs
                    variant="unstyled"
                    isFitted
                    size="md"
                    defaultIndex={0}
                >
                    <TabList
                        mb={10}
                        bg={tabBg}
                        p={1.5}
                        borderRadius="full"
                        borderWidth="1px"
                        borderColor={cardBorder}
                        boxShadow="0 4px 20px rgba(0,0,0,0.08)"
                        maxW="500px"
                        mx="auto"
                    >
                        <Tab
                            _selected={{
                                bg: tabSelectedBg,
                                color: tabSelectedColor,
                                boxShadow: "0 4px 15px rgba(59, 130, 246, 0.4)",
                            }}
                            _hover={{
                                color: tabHoverColor,
                            }}
                            color={tabColor}
                            borderRadius="full"
                            py={3}
                            px={6}
                            fontWeight={600}
                            fontSize={{ base: "sm", md: "md" }}
                            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                        >
                            <Flex align="center" gap={2}>
                                <Icon as={FaTools} />
                                <Text>Technical Skills</Text>
                            </Flex>
                        </Tab>
                        <Tab
                            _selected={{
                                bg: tabSelectedBg,
                                color: tabSelectedColor,
                                boxShadow: "0 4px 15px rgba(59, 130, 246, 0.4)",
                            }}
                            _hover={{
                                color: tabHoverColor,
                            }}
                            color={tabColor}
                            borderRadius="full"
                            py={3}
                            px={6}
                            fontWeight={600}
                            fontSize={{ base: "sm", md: "md" }}
                            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                        >
                            <Flex align="center" gap={2}>
                                <Icon as={FaBrain} />
                                <Text>Soft Skills</Text>
                            </Flex>
                        </Tab>
                    </TabList>

                    <TabPanels>
                        {/* Technical Skills Panel */}
                        <TabPanel p={0} animation={`${fadeInUp} 0.5s ease-out`}>
                            <Grid
                                gridTemplateColumns={[
                                    "repeat(2, 1fr)",
                                    "repeat(3, 1fr)",
                                    "repeat(4, 1fr)",
                                    "repeat(5, 1fr)",
                                    "repeat(7, 1fr)",
                                ]}
                                gap={{ base: 4, md: 6 }}
                            >
                                {techSkills.map((skill, index) => (
                                    <Box
                                        key={skill.name}
                                        bg={cardBg}
                                        borderRadius="2xl"
                                        borderWidth="1px"
                                        borderColor={cardBorder}
                                        p={{ base: 4, md: 6 }}
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                        justifyContent="center"
                                        gap={3}
                                        animation={`${fadeInUp} 0.5s ease-out ${index * 0.05}s both`}
                                        transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                                        position="relative"
                                        overflow="hidden"
                                        cursor="pointer"
                                        _hover={{
                                            transform: "translateY(-8px) scale(1.05)",
                                            boxShadow: `0 20px 40px ${skill.color}30`,
                                            borderColor: skill.color,
                                        }}
                                        _before={{
                                            content: '""',
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            height: "4px",
                                            bg: skill.color,
                                            transform: "scaleX(0)",
                                            transition: "transform 0.3s ease",
                                        }}
                                        _hoverBefore={{
                                            transform: "scaleX(1)",
                                        }}
                                    >
                                        <Box
                                            animation={`${float} ${3 + index * 0.2}s ease-in-out infinite`}
                                            transition="all 0.3s ease"
                                            _hover={{
                                                transform: "scale(1.2) rotate(10deg)",
                                            }}
                                        >
                                            <skill.icon 
                                                size="40px" 
                                                color={skill.color}
                                                style={{
                                                    filter: `drop-shadow(0 4px 8px ${skill.color}40)`
                                                }}
                                            />
                                        </Box>
                                        <Text
                                            fontSize={{ base: "xs", md: "sm" }}
                                            fontWeight={600}
                                            color={titleColor}
                                            textAlign="center"
                                        >
                                            {skill.name}
                                        </Text>
                                    </Box>
                                ))}
                            </Grid>
                        </TabPanel>

                        {/* Soft Skills Panel */}
                        <TabPanel p={0} animation={`${fadeInUp} 0.5s ease-out`}>
                            <Grid
                                gridTemplateColumns={[
                                    "repeat(1, 1fr)",
                                    "repeat(2, 1fr)",
                                    "repeat(3, 1fr)",
                                ]}
                                gap={{ base: 4, md: 6 }}
                            >
                                {softSkills.map((skill, index) => (
                                    <Box
                                        key={skill.name}
                                        bg={cardBg}
                                        borderRadius="2xl"
                                        borderWidth="1px"
                                        borderColor={cardBorder}
                                        p={{ base: 5, md: 6 }}
                                        animation={`${fadeInUp} 0.5s ease-out ${index * 0.1}s both`}
                                        transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                                        position="relative"
                                        overflow="hidden"
                                        cursor="pointer"
                                        _hover={{
                                            transform: "translateY(-5px)",
                                            boxShadow: `0 20px 40px rgba(0,0,0,0.1)`,
                                            borderColor: skill.color,
                                        }}
                                        _before={{
                                            content: '""',
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            width: "4px",
                                            height: "100%",
                                            bg: skill.color,
                                            transform: "scaleY(0)",
                                            transition: "transform 0.3s ease",
                                        }}
                                        _hoverBefore={{
                                            transform: "scaleY(1)",
                                        }}
                                    >
                                        <HStack spacing={4} mb={3}>
                                            <Box
                                                p={3}
                                                bg={skill.color.includes('.') ? `${skill.color.split('.')[0]}.100` : 'gray.100'}
                                                borderRadius="xl"
                                                animation={`${float} ${3 + index * 0.2}s ease-in-out infinite`}
                                            >
                                                <Icon 
                                                    as={skill.icon} 
                                                    w={6} 
                                                    h={6} 
                                                    color={skill.color}
                                                />
                                            </Box>
                                            <Box flex={1}>
                                                <Text
                                                    fontSize={{ base: "md", md: "lg" }}
                                                    fontWeight={700}
                                                    color={titleColor}
                                                >
                                                    {skill.name}
                                                </Text>
                                                <Text
                                                    fontSize="sm"
                                                    color={textColor}
                                                >
                                                    {skill.desc}
                                                </Text>
                                            </Box>
                                        </HStack>

                                    </Box>
                                ))}
                            </Grid>
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </Box>
        </Box>
    );
};

export default Techstack;
